import { useState } from "react";
import { logoutUser } from "../authService";
import { useLanguage } from "../i18n/LanguageProvider";
import { isAppleAuthCancellation } from "../services/appleAuth";
import { linkExistingPasswordAccountToApple } from "../services/appleAccountLinking";
import ProfileCompletionPage from "./ProfileCompletionPage";

const NEW_ACCOUNT_CHOICE = "new";

export function appleOnboardingStorageKey(uid) {
  return `vaultquest:appleOnboarding:${String(uid || "")}`;
}

function readChoice(uid) {
  try {
    return window.sessionStorage?.getItem(appleOnboardingStorageKey(uid)) || "";
  } catch {
    return "";
  }
}

function writeChoice(uid, choice) {
  try {
    if (choice) {
      window.sessionStorage?.setItem(appleOnboardingStorageKey(uid), choice);
    } else {
      window.sessionStorage?.removeItem(appleOnboardingStorageKey(uid));
    }
  } catch {
    // Restricted storage must not block Apple onboarding.
  }
}

function mapLinkError(error, t) {
  const code = String(error?.code || "");
  const message = String(error?.message || "");
  if (isAppleAuthCancellation(error)) return "";
  if (
    code.includes("invalid-credential") ||
    code.includes("wrong-password") ||
    code.includes("invalid-login-credentials") ||
    message.includes("INVALID_LOGIN")
  ) return t("appleOnboarding.errors.invalidCredentials");
  if (code.includes("too-many-requests")) return t("login.errors.tooManyRequests");
  if (code.includes("network")) return t("login.errors.network");
  if (
    code.includes("provider-already-linked") ||
    code.includes("credential-already-in-use") ||
    message.includes("TARGET_APPLE_PROVIDER_EXISTS")
  ) return t("appleOnboarding.errors.appleAlreadyLinked");
  return t("appleOnboarding.errors.generic");
}

export default function AppleAccountOnboardingPage({ firebaseUser }) {
  const { t } = useLanguage();
  const uid = firebaseUser?.uid || "";
  const [choice, setChoice] = useState(() => readChoice(uid));
  const [linking, setLinking] = useState(false);
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  if (choice === NEW_ACCOUNT_CHOICE) return <ProfileCompletionPage />;

  const chooseNewAccount = () => {
    writeChoice(uid, NEW_ACCOUNT_CHOICE);
    setChoice(NEW_ACCOUNT_CHOICE);
  };

  const handleLink = async () => {
    if (linking) return;
    if (!identifier.trim() || !password) {
      setError(t("appleOnboarding.errors.missingCredentials"));
      return;
    }

    setError("");
    setLinking(true);
    try {
      await linkExistingPasswordAccountToApple({ identifier, password });
      writeChoice(uid, "");
    } catch (linkError) {
      setError(mapLinkError(linkError, t));
    } finally {
      setLinking(false);
    }
  };

  return (
    <main className="vq-profile-completion vq-apple-onboarding">
      <section className="vq-profile-completion__card vq-apple-onboarding__card">
        <div className="vq-apple-onboarding__mark" aria-hidden="true"></div>
        <div className="vq-apple-onboarding__eyebrow">{t("appleOnboarding.eyebrow")}</div>
        <h1>{t("appleOnboarding.title")}</h1>
        <p className="vq-profile-completion__intro">{t("appleOnboarding.description")}</p>

        {choice === "link" ? (
          <div className="vq-apple-onboarding__link-form">
            <label>
              <span>{t("login.emailOrUsernamePlaceholder")}</span>
              <input
                value={identifier}
                autoCapitalize="none"
                autoComplete="username"
                onChange={(event) => setIdentifier(event.target.value)}
                disabled={linking}
              />
            </label>
            <label>
              <span>{t("login.passwordPlaceholder")}</span>
              <input
                type="password"
                value={password}
                autoComplete="current-password"
                onChange={(event) => setPassword(event.target.value)}
                disabled={linking}
              />
            </label>
            <p className="vq-apple-onboarding__privacy">{t("appleOnboarding.linkHint")}</p>
            {error && <div className="vq-profile-completion__error" role="alert">{error}</div>}
            <button
              type="button"
              className="vq-profile-completion__submit"
              onClick={handleLink}
              disabled={linking}
            >
              {linking ? t("appleOnboarding.linking") : t("appleOnboarding.confirmLink")}
            </button>
            <button
              type="button"
              className="vq-profile-completion__cancel"
              onClick={() => { setChoice(""); setError(""); }}
              disabled={linking}
            >
              {t("common.back")}
            </button>
          </div>
        ) : (
          <div className="vq-apple-onboarding__actions">
            <button
              type="button"
              className="vq-profile-completion__submit"
              onClick={() => setChoice("link")}
            >
              {t("appleOnboarding.linkExisting")}
            </button>
            <button
              type="button"
              className="vq-profile-completion__cancel"
              onClick={chooseNewAccount}
            >
              {t("appleOnboarding.createNew")}
            </button>
            <button
              type="button"
              className="vq-apple-onboarding__logout"
              onClick={() => logoutUser()}
            >
              {t("common.cancel")}
            </button>
          </div>
        )}
      </section>
    </main>
  );
}
